import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { describeResult, isSuccess, requestPackingList } from '../lib/packingListRequest';
import { fromWorkspace, workspaceKey } from '../lib/packingListWire';
import { workspaceFailed, workspaceLoaded, workspaceRequested } from '../store/packingListSlice';

const EMPTY_VIEW = { house: null, documents: [], items: [], allocations: {}, allocationVersion: 0, documentHints: {} };

/** 조회 번호. 화면 인스턴스와 무관하게 계속 커져야 스토어가 House 단위로 옛 응답을 가려낼 수 있다. */
let nextRequestId = 1;

/**
 * House 한 건의 PL 작업 영역(문서 카드·확정 품목·배분)을 서버에서 읽는다.
 * 화면을 열 때, 저장·확정·삭제 뒤, 팀원의 변경 알림이 올 때, 연결이 복구될 때 다시 읽는다.
 * 마지막 조회 결과는 House별로 스토어에 남아 화면에 다시 들어오면 그 내용부터 보여준다.
 */
export default function usePackingListDocuments({ tab, shipmentId, houseId }) {
    const dispatch = useDispatch();
    const key = workspaceKey(tab, houseId);
    const ws = useSelector((s) => s.packingList.workspaces[key]);
    const changeSeq = useSelector((s) => s.packingList.changeSeq[key] ?? 0);
    const connected = useSelector((s) => s.socketStatus.socketStatus);
    const connectionEpoch = useSelector((s) => s.socketStatus.connectionEpoch);
    const scope = useMemo(() => ({ tab, shipmentId, houseId }), [tab, shipmentId, houseId]);

    const reload = useCallback(async () => {
        const requestId = nextRequestId;
        nextRequestId += 1;
        dispatch(workspaceRequested({ key, requestId }));
        const result = await requestPackingList({ action: 'read', operation: 'workspace', scope });
        if (isSuccess(result)) dispatch(workspaceLoaded({ key, requestId, data: fromWorkspace(result.data) }));
        else dispatch(workspaceFailed({ key, requestId, error: describeResult(result), code: result.code }));
    }, [dispatch, key, scope]);

    useEffect(() => {
        if (connected) reload();
    }, [reload, connected, changeSeq, connectionEpoch]);

    return { ...(ws?.data ?? EMPTY_VIEW), key, scope, status: ws?.status ?? 'idle', error: ws?.error ?? null, reload };
}
